import React, { useState, useEffect } from 'react';
import Splash from '../../../../splash/Splash';
import { fetchSliders, fetchSocialLinks, fetchGeneral, fetchPaymentMethods, fetchPromotions, fetchUserData, fetchTransactions, fetchTurnovers, fetchMarketBlock } from '../../../../api';

function APIS({ setGlobalData, refreshPvtData }) {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
    const [isLoadingPublic, setIsLoadingPublic] = useState(true);
    const [isLoadingPrivate, setIsLoadingPrivate] = useState(isLoggedIn);

    const [apiData, setApiData] = useState({
        sliders: { main: [], favourite: [], feature: [] },
        socialLinks: [],
        general: {},
        paymentMethods: [],
        withdrawMethods: [],
        hasEWallet: false,
        promotions: { activePromotion: [], displayPromotion: [], allPromotions: [] },
        userData: null,
        transactions: [],
        turnovers: [],
        marketBlock: [],
    });

    // Fetch Public APIs (No login required)
    useEffect(() => {
        let cancelled = false;
        const fetchPublicData = async () => {
            setIsLoadingPublic(true);

            try {
                const [slidersData, socialData, generalData, paymentData, promotionsData] = await Promise.all([
                    fetchSliders(),
                    fetchSocialLinks(),
                    fetchGeneral(),
                    fetchPaymentMethods(),
                    fetchPromotions(),
                ]);
                if (cancelled) return;

                let pgs = [];
                let hasEWallet = false;
                if (paymentData?.type === 'ewallet') {
                    pgs = paymentData?.pgs;
                    hasEWallet = true;
                } else {
                    pgs = paymentData;
                }

                setApiData(prevData => ({
                    ...prevData,
                    hasEWallet,
                    sliders: {
                        main: (slidersData || []).filter(s => s.status === 'active' && s.slider_type === 'Main'),
                        favourite: (slidersData || []).filter(s => s.status === 'active' && s.slider_type === 'Favourite'),
                        feature: (slidersData || []).filter(s => s.status === 'active' && s.slider_type === 'Features'),
                    },
                    socialLinks: socialData?.[0] || {},
                    general: generalData?.[0] || {},
                    paymentMethods: pgs,
                    promotions: {
                        activePromotion: (promotionsData || []).filter(p => p.status === 'active'),
                        displayPromotion: (promotionsData || []).filter(p => p.status === 'display'),
                        allPromotions: promotionsData || [],
                    },
                }));
            } catch (error) {
                // Non-critical – data may be loaded by other means
            } finally {
                if (!cancelled) setIsLoadingPublic(false);
            }
        };

        fetchPublicData();
        return () => { cancelled = true; };
    }, []);

    // Fetch Private APIs (Login required)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const remoteId = localStorage.getItem('remote_id');
        if (!isLoggedIn || !token || !remoteId) return;

        let cancelled = false;
        const fetchPrivateData = async () => {
            setIsLoadingPrivate(true);
            try {
                const [userData, transactions, turnovers, marketBlock] = await Promise.all([
                    fetchUserData(remoteId),
                    fetchTransactions(remoteId),
                    fetchTurnovers(remoteId),
                    fetchMarketBlock(),
                ]);
                if (cancelled) return;

                setApiData(prevData => ({
                    ...prevData,
                    userData,
                    transactions,
                    turnovers: turnovers?.turnover || [],
                    marketBlock: marketBlock || [],
                }));
            } catch (error) {
                // Private data fetch failure – user can still use public parts
            } finally {
                if (!cancelled) setIsLoadingPrivate(false);
            }
        };

        fetchPrivateData();
        return () => { cancelled = true; };
    }, [isLoggedIn, refreshPvtData]);

    // Pass API data to global state in App.js
    useEffect(() => {
        setGlobalData(apiData);
    }, [apiData, setGlobalData]);

    // Loading State Handling
    if (isLoadingPublic) {
        return <Splash />;
    }

    return null;
}

export default APIS;
